import React, { useMemo, useState } from "react";
import { Modal } from "antd";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Fab, Sheet,
  ActionRow, DetailRow, Field, TextInput, TextAreaInput, SelectInput, SwitchRow,
  Pill, Mono, Ico, fmt, useMobileMenu,
} from "../produits/productMobileKit";
import { useSelection } from "../../SelectionContext";
import { useAccountMasters } from "../../gestion-logistics/hooks/useAccountMasters";
import { useLogisticsPermissions } from "../../gestion-logistics/hooks/useLogisticsPermissions";
import type { AccountMaster } from "../../gestion-logistics/types/costing.types";

const emptyForm = () => ({ account_type_code: "", code: "", name: "", description: "", default_unit: "", default_unit_price: "0", tax_applicable: false, is_active: true });
const truthy = (value: unknown) => value === true || value === 1 || value === "1";

export default function AccountMastersMobile() {
  const onMenu = useMobileMenu();
  const { selectedCompany } = useSelection();
  const permissions = useLogisticsPermissions();
  const { accountMasters, accountTypes, loading, createAccountMaster, updateAccountMaster, deleteAccountMaster, toggleAccountMasterStatus } = useAccountMasters();
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [detail, setDetail] = useState<AccountMaster | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AccountMaster | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return accountMasters.filter((row) => {
      if (type !== "all" && row.account_type_code !== type) return false;
      return !needle || `${row.code} ${row.name} ${row.description || ""}`.toLowerCase().includes(needle);
    });
  }, [accountMasters, query, type]);

  const openForm = (row: AccountMaster | null = null) => {
    setDetail(null);
    setEditing(row);
    setForm(row ? {
      account_type_code: row.account_type_code || "", code: row.code || "", name: row.name || "",
      description: row.description || "", default_unit: row.default_unit || "",
      default_unit_price: String(row.default_unit_price || 0), tax_applicable: truthy(row.tax_applicable),
      is_active: truthy(row.is_active),
    } : emptyForm());
    setFormOpen(true);
  };

  const submit = async () => {
    const selectedType = accountTypes.find((item) => item.code === form.account_type_code);
    if (!selectedType || !form.code.trim() || !form.name.trim()) return;
    setSaving(true);
    const payload = {
      account_type_id: selectedType.id,
      code: form.code.trim().toUpperCase(), name: form.name.trim(), description: form.description.trim(),
      default_unit: form.default_unit.trim(), default_unit_price: Number(form.default_unit_price) || 0,
      tax_applicable: form.tax_applicable, is_active: form.is_active,
    };
    const success = editing ? await updateAccountMaster(editing.id, payload) : Boolean(await createAccountMaster(payload));
    setSaving(false);
    if (success) { setFormOpen(false); setEditing(null); }
  };

  const remove = (row: AccountMaster) => Modal.confirm({
    title: "Supprimer cette rubrique ?", content: row.name, okText: "Supprimer", okType: "danger", cancelText: "Annuler", centered: true,
    onOk: () => deleteAccountMaster(row.id),
  });

  if (!permissions.canViewAccountMasters) return <ProductScreen><Appbar title="Configuration logistique" onMenu={onMenu} /><div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" /></div></ProductScreen>;
  if (!selectedCompany) return <ProductScreen><Appbar title="Configuration logistique" onMenu={onMenu} /><div className="body"><EmptyState icon="store" title="Entreprise requise" text="Sélectionnez une entreprise." /></div></ProductScreen>;

  return (
    <ProductScreen>
      <Appbar title="Configuration logistique" subtitle={`${fmt(accountMasters.length)} rubrique${accountMasters.length > 1 ? "s" : ""}`} onMenu={onMenu} />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[{ label: "Total", value: fmt(accountMasters.length) }, { label: "Actives", value: fmt(accountMasters.filter((row) => truthy(row.is_active)).length) }, { label: "Types", value: fmt(accountTypes.length) }]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Code, nom ou description…" />
        <Chips value={type} onChange={setType} options={[{ key: "all", label: "Toutes" }, ...accountTypes.map((item) => ({ key: item.code, label: item.name }))]} />
      </div>
      <div className="body">
        {loading && !accountMasters.length ? <EmptyState loading title="Chargement…" /> : !visible.length ? <EmptyState icon="settings" title="Aucune rubrique" /> : <div className="list">
          {visible.map((row) => <button key={row.id} className="rcard" onClick={() => setDetail(row)}>
            <Mono name={row.code} square /><div className="rcard__main"><div className="rcard__name">{row.name}</div><div className="rcard__meta"><Pill tone={truthy(row.is_active) ? "ok" : "off"} label={truthy(row.is_active) ? "Active" : "Inactive"} /><span className="rcard__sub">{row.account_type_name || row.account_type_code}</span></div></div><span className="rcard__chev"><Ico name="chevron-right" /></span>
          </button>)}
        </div>}
      </div>
      {permissions.canCreateAccountMasters ? <Fab label="Nouvelle rubrique" onClick={() => openForm()} /> : null}

      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.name} subtitle={detail?.code}>
        {detail ? <><DetailRow label="Type" value={detail.account_type_name || detail.account_type_code || "—"} /><DetailRow label="Description" value={detail.description || "—"} /><DetailRow label="Unité" value={detail.default_unit || "—"} /><DetailRow label="Prix unitaire" value={fmt(detail.default_unit_price || 0)} /><DetailRow label="Taxe" value={truthy(detail.tax_applicable) ? "Applicable" : "Non applicable"} />
          {permissions.canEditAccountMasters ? <ActionRow icon="pencil" label="Modifier" onClick={() => openForm(detail)} /> : null}
          {permissions.canEditAccountMasters ? <ActionRow icon="rotate-ccw" label={truthy(detail.is_active) ? "Désactiver" : "Activer"} onClick={async () => { await toggleAccountMasterStatus(detail.id); setDetail(null); }} /> : null}
          {permissions.canDeleteAccountMasters ? <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => remove(detail)} /> : null}</> : null}
      </Sheet>

      <Sheet open={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Modifier la rubrique" : "Nouvelle rubrique"}>
        <Field label="Type de compte *">{editing ? <TextInput value={accountTypes.find((item) => item.code === form.account_type_code)?.name || form.account_type_code} onChange={() => {}} readOnly /> : <SelectInput value={form.account_type_code} onChange={(account_type_code: string | null) => setForm((f) => ({ ...f, account_type_code: account_type_code || "" }))} options={accountTypes.map((item) => ({ value: item.code, label: item.name }))} />}</Field>
        <Field label="Code *"><TextInput value={form.code} onChange={(code: string) => setForm((f) => ({ ...f, code: code.toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 20) }))} /></Field>
        <Field label="Nom *"><TextInput value={form.name} onChange={(name: string) => setForm((f) => ({ ...f, name }))} /></Field>
        <Field label="Description"><TextAreaInput value={form.description} onChange={(description: string) => setForm((f) => ({ ...f, description }))} rows={3} /></Field>
        <Field label="Unité par défaut"><TextInput value={form.default_unit} onChange={(default_unit: string) => setForm((f) => ({ ...f, default_unit }))} /></Field>
        <Field label="Prix unitaire"><TextInput value={form.default_unit_price} onChange={(default_unit_price: string) => setForm((f) => ({ ...f, default_unit_price }))} inputMode="decimal" /></Field>
        <SwitchRow label="Taxe applicable" value={form.tax_applicable} onChange={(tax_applicable: boolean) => setForm((f) => ({ ...f, tax_applicable }))} />
        <SwitchRow label="Rubrique active" value={form.is_active} onChange={(is_active: boolean) => setForm((f) => ({ ...f, is_active }))} />
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormOpen(false)}>Annuler</button><button className="btn btn--primary" onClick={submit} disabled={saving || !form.account_type_code || !form.code.trim() || !form.name.trim()}>{saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>
    </ProductScreen>
  );
}
